import React, { useEffect, useRef, useState, useId } from 'react';
import { AlertTriangle, Mic, Trash2, Play, Pause, Loader2 } from 'lucide-react';
import { useClonedVoices, useDeleteClonedVoice } from '../../http-hooks/ai-voices/useClonedVoice';
import { useGlobalStore } from '../../zustand-store/useGlobalStore';
import Spinner from '../Spinner';
import { toast } from 'react-toastify';
import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore';
import { C } from '../../styles/tokens';
import ConfirmDialog from '../media/ConfirmDialog';
import { getVoicePreviewUrl } from '../../utils/voicePreview';
import { languageNameFromCode } from '../../utils/languageDisplay';
import { getVoiceTypeMeta } from '../../constants/voiceCreationType';

interface MyVoicesProps {
    org_id: string;
    integration_id: string;
    searchQuery?: string;
}

const GRADIENTS = [
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#fa709a,#fee140)',
    'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];

// How long a voice can sit in 'processing' before the card flags it as taking unusually
// long — matches the old client-side poll timeout (40 attempts * 3s) that lived in
// InstantVoiceClone/CreateVoiceDialog before polling moved into useClonedVoices.
const STUCK_PROCESSING_MS = 2 * 60 * 1000;

// Maps the cloned_voice `type` to the quality-rating tool/source (see
// constants/qualityRatingCopy). Absent/unknown type = an instant clone.
const VOICE_QUALITY_TOOL: Record<string, string> = {
    cloned: 'voice_clone',
    design: 'voice_design',
    mix: 'voice_mix',
};

function VoiceAvatar({ name, gradient }: { name: string; gradient: string }) {
    return (
        <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, color: '#fff',
        }}>
            {name?.[0]?.toUpperCase() || '?'}
        </div>
    );
}

export const MyVoices: React.FC<MyVoicesProps> = ({ org_id, integration_id, searchQuery = '' }) => {
    const { data: voices, isLoading } = useClonedVoices({ org_id, integration_id, pollWhileProcessing: true });
    const deleteVoiceMutation = useDeleteClonedVoice();
    const requestQualityRating = useGlobalStore((state) => state.requestQualityRating);
    const listId = useId();
    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

    // First-seen time per processing voice id, used to flag ones that have been stuck for
    // a while. Only tracked for the lifetime of this mount — resets if the My Voices tab
    // unmounts and remounts, which is an acceptable trade-off for a client-only signal.
    const firstSeenProcessingRef = useRef<Map<string, number>>(new Map());

    const prevStatusRef = useRef<Map<string, string>>(new Map());
    useEffect(() => {
        const prevStatus = prevStatusRef.current;
        const nextStatus = new Map<string, string>();
        for (const voice of (voices || [])) {
            const wasStatus = prevStatus.get(voice.id);
            if (voice.status === 'failed' && wasStatus && wasStatus !== 'failed') {
                const tm = getVoiceTypeMeta(voice.type);
                toast.error(`"${voice.voice_name}" — voice ${tm.noun} failed. Please try again.`);
            }
            // Freshly finished (processing → ready): ask for a quality rating.
            if (voice.status === 'ready' && wasStatus === 'processing') {
                requestQualityRating({
                    tool: VOICE_QUALITY_TOOL[voice.type ?? 'cloned'] || 'voice_clone',
                    source: VOICE_QUALITY_TOOL[voice.type ?? 'cloned'] || 'voice_clone',
                    orgId: org_id,
                    integrationId: integration_id,
                    meta: { voice_id: voice.id, duration_sec: voice.audio_duration, language: voice.language_code },
                });
            }
            nextStatus.set(voice.id, voice.status || 'ready');
        }
        prevStatusRef.current = nextStatus;
    }, [voices, org_id, integration_id, requestQualityRating]);

    const visibleVoices = voices || [];

    const filteredVoices = searchQuery.trim()
        ? visibleVoices.filter((voice: any) =>
            voice.voice_name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : visibleVoices;

    const handleDeleteVoice = async (voiceId: string) => {
        setPendingDelete(null);
        try {
            await deleteVoiceMutation.mutateAsync({ org_id, integration_id, voice_id: voiceId });
            toast.success('Voice deleted successfully!');
        } catch (error) {
            console.error('Error deleting voice:', error);
            toast.error('Failed to delete voice. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spinner color="var(--bs-primary)" />
            </div>
        );
    }

    if (!filteredVoices || filteredVoices.length === 0) {
        if (searchQuery.trim()) {
            return (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <p style={{ fontSize: '14px', color: '#90a0b7' }}>No voices found matching "{searchQuery}"</p>
                </div>
            );
        }

        return (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'rgba(var(--bs-primary-rgb), 0.08)',
                    border: '1px solid rgba(var(--bs-primary-rgb), 0.14)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <Mic size={28} color="var(--bs-primary)" />
                </div>
                <h5 style={{ fontSize: '16px', fontWeight: 600, color: '#1a1f36', marginBottom: '8px' }}>No cloned voices yet</h5>
                <p style={{ fontSize: '13px', color: '#90a0b7', marginBottom: 0 }}>Create your first cloned voice to see it here</p>
            </div>
        );
    }

    return (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px,1fr))', gap: 16 }}>
            {filteredVoices.map((voice: any, i: number) => {
                const isProcessing = voice.status === 'processing';
                const isFailed = voice.status === 'failed';
                const audioURL = getVoicePreviewUrl(voice) || '';
                const instanceKey = `${listId}-${voice.id}`;
                const active = isPlaying && activeKey === instanceKey;
                const isLoadingAudio = !!audioURL && activeKey === instanceKey && !!loadingSrc;
                const meta = [languageNameFromCode(voice.language_code)].filter(Boolean).join(' · ');
                const tm = getVoiceTypeMeta(voice.type);
                const TypeIcon = tm.Icon;

                if (isProcessing) {
                    if (!firstSeenProcessingRef.current.has(voice.id)) {
                        firstSeenProcessingRef.current.set(voice.id, Date.now());
                    }
                    const firstSeen = firstSeenProcessingRef.current.get(voice.id)!;
                    const isStuck = Date.now() - firstSeen > STUCK_PROCESSING_MS;

                    return (
                        <div key={voice.id}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 14px',
                                border: `1px solid ${C.n100}`,
                                borderRadius: 12,
                                background: C.w,
                            }}>
                                <div style={{
                                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                                    background: 'rgba(var(--bs-primary-rgb), 0.08)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Loader2 size={18} color="var(--bs-primary)" className="mv-spin" />
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {voice.voice_name}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: C.n400, marginTop: 3 }}>
                                        {isStuck ? 'Taking longer than expected…' : `${tm.verb} your voice…`}
                                    </div>
                                </div>

                                <button
                                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: voice.id, name: voice.voice_name }); }}
                                    style={{
                                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                        border: `1px solid ${C.n200}`,
                                        background: C.w,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: C.n400,
                                        transition: 'all .15s',
                                    }}
                                    title="Cancel / delete"
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#FEE2E2';
                                        e.currentTarget.style.borderColor = '#FCA5A5';
                                        e.currentTarget.style.color = '#DC2626';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = C.w;
                                        e.currentTarget.style.borderColor = C.n200;
                                        e.currentTarget.style.color = C.n400;
                                    }}
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    );
                }

                if (isFailed) {
                    return (
                        <div key={voice.id}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 14px',
                                border: '1px solid #FCA5A5',
                                borderRadius: 12,
                                background: '#FEF2F2',
                            }}>
                                <div style={{
                                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                                    background: 'rgba(220,38,38,0.10)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <AlertTriangle size={18} color="#DC2626" />
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {voice.voice_name}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: '#DC2626', marginTop: 3 }}>
                                        Voice {tm.noun} failed. Please try again.
                                    </div>
                                </div>

                                <button
                                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: voice.id, name: voice.voice_name }); }}
                                    style={{
                                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                        border: '1px solid #FCA5A5',
                                        background: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: '#DC2626',
                                        transition: 'all .15s',
                                    }}
                                    title="Delete"
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={voice.id}>
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 14px',
                                border: `1px solid ${C.n100}`,
                                borderRadius: 12,
                                background: C.w,
                                transition: 'box-shadow .15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.07)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                        >
                            <VoiceAvatar name={voice.voice_name} gradient={GRADIENTS[i % GRADIENTS.length]} />

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {voice.voice_name}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                                        fontSize: 10, fontWeight: 600, lineHeight: 1,
                                        color: tm.color, background: tm.bg,
                                        padding: '3px 6px', borderRadius: 5,
                                    }}>
                                        <TypeIcon size={10} /> {tm.label}
                                    </span>
                                    <span style={{ fontSize: 11.5, color: C.n400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {meta}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={() => audioURL && !isLoadingAudio && togglePlayPause(audioURL, instanceKey)}
                                disabled={!audioURL || isLoadingAudio}
                                style={{
                                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                    border: `1px solid ${active ? C.n900 : C.n200}`,
                                    background: active ? C.n900 : C.w,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: audioURL && !isLoadingAudio ? 'pointer' : 'not-allowed',
                                    transition: 'all .15s',
                                }}
                                title={isLoadingAudio ? 'Loading…' : active ? 'Pause' : 'Preview'}
                            >
                                {isLoadingAudio
                                    ? <Loader2 size={13} color={active ? C.w : C.n700} className="mv-spin" />
                                    : active
                                        ? <Pause size={13} color={C.w} />
                                        : <Play size={13} color={C.n700} />
                                }
                            </button>

                            <button
                                onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: voice.id, name: voice.voice_name }); }}
                                style={{
                                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                    border: `1px solid ${C.n200}`,
                                    background: C.w,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', color: C.n400,
                                    transition: 'all .15s',
                                }}
                                title="Delete"
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#FEE2E2';
                                    e.currentTarget.style.borderColor = '#FCA5A5';
                                    e.currentTarget.style.color = '#DC2626';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = C.w;
                                    e.currentTarget.style.borderColor = C.n200;
                                    e.currentTarget.style.color = C.n400;
                                }}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
        <ConfirmDialog
            open={!!pendingDelete}
            title="Delete voice"
            message={pendingDelete ? `Are you sure you want to delete "${pendingDelete.name}"?` : ''}
            onConfirm={() => pendingDelete && handleDeleteVoice(pendingDelete.id)}
            onCancel={() => setPendingDelete(null)}
        />
        </>
    );
};
